/**
 * Un micro ouvert qui ne capte aucun son : coupé par l'ordinateur (la touche muet du clavier, le volume
 * d'entrée à zéro), pris par une autre app. L'app le croit ouvert, la barre le montre ouvert, et le
 * masteur n'entend rien.
 *
 * Ce qui le trahit, c'est le **silence numérique** : un vrai micro, même dans une pièce calme, rend un
 * souffle (des échantillons minuscules, jamais nuls) ; un micro coupé rend des zéros exacts. La règle
 * de l'ancien diagnostic (`useMicSilentDetection` : « le niveau reste à 0 ») lue sur les échantillons
 * eux-mêmes : seul un silence exact et continu compte, `MIC_SILENT_MS` durant. Une lecture impossible
 * (le navigateur n'a pas encore laissé démarrer l'analyse du son) ne dit rien, et ne compte pas.
 */

/** Le silence exact au-delà duquel le micro est dit muet. L'ancien diagnostic : 1,5 s. */
export const MIC_SILENT_MS = 3000

export type MicSilenceState = { silentSince: number | null; silent: boolean }

export type MicSilenceEvent =
    /** Une lecture : le plus fort échantillon, en valeur absolue ; `null` : rien de lisible. */
    | { type: "read"; at: number; peak: number | null }
    /** Le micro coupé, le Concentré, la piste changée : on repart de zéro. */
    | { type: "reset" }

export const initialMicSilence: MicSilenceState = { silentSince: null, silent: false }

export function micSilenceReducer(state: MicSilenceState, event: MicSilenceEvent): MicSilenceState {
    if (event.type === "reset" || event.peak == null || event.peak > 0) {
        return state.silentSince == null && !state.silent ? state : initialMicSilence
    }
    const silentSince = state.silentSince ?? event.at
    const silent = event.at - silentSince >= MIC_SILENT_MS
    return silentSince === state.silentSince && silent === state.silent ? state : { silentSince, silent }
}

/** Le plus fort échantillon d'une lecture, en valeur absolue. */
export function peakOf(samples: ArrayLike<number>): number {
    let peak = 0
    for (let i = 0; i < samples.length; i++) {
        const value = Math.abs(samples[i])
        if (value > peak) peak = value
    }
    return peak
}
